/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/alt-text, jsx-a11y/label-has-associated-control */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import BeneficiaryProfileForm from "../component/BeneficiaryProfileForm";
import BeneficiarySidebarProfile from "../component/BeneficiarySidebarProfile";
import BeneficiaryFamilyDetailsForm from "../component/BeneficiaryFamilyDetailsForm";
import BeneficiaryFinancialInfoForm from "../component/BeneficiaryFinancialInfoForm";
import RequestChatModal from "../component/RequestChatModal";
import AppointmentManager from "../component/AppointmentManager";
import BeneficiaryAccountSettings from "../component/BeneficiaryAccountSettings";
import { getApiUrl } from "../api";
import { getStoredToken, getStoredUser, redirectToLogin } from "../auth";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { getCustomerRequestNumber } from "../utils/requestReferences";
import { getCustomerDocumentOutcome, getDocumentLoadingCopy } from "../utils/documentValidationChecks";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";
import { AidTrackingResult } from "./TrackAidRequestStatus";
import { Link } from "../component/AppLink";
import AssistanceRequestNotice from "../component/AssistanceRequestNotice";

const UNVERIFIED_ACCESSIBLE_TABS = new Set([
  "dashboard-tab",
  "profile-tab",
  "family-tab",
  "financial-tab",
]);

const activateDashboardTab = (root, targetTabId) => {
  if (!targetTabId) return;

  root.querySelectorAll(".beni-menu").forEach((button) => {
    button.classList.toggle("active", button.dataset.tab === targetTabId);
  });
  root.querySelectorAll(".beni-tab-content").forEach((content) => {
    content.classList.toggle("active", content.id === targetTabId);
  });

  const targetContent = root.querySelector(`#${CSS.escape(targetTabId)}`);
  if (targetContent) {
    targetContent.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const setMobileMenuOpen = (root, isOpen) => {
  const layout = root.querySelector(".benifi-dash-bord");
  const toggle = root.querySelector(".beni-mobile-menu-toggle");
  if (!layout || !toggle) return;

  layout.classList.toggle("beni-menu-open", isOpen);
  toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
  toggle.innerHTML = isOpen
    ? '<i class="fa-solid fa-xmark"></i> Close Menu'
    : '<i class="fa-solid fa-bars"></i> Beneficiary Menu';
};

const appendUploadedFiles = (root, files) => {
  const fileList = root.querySelector("#fileList");
  if (!fileList) return;

  Array.from(files || []).forEach((file) => {
    const item = document.createElement("div");
    item.className = "doc-file-item";

    const left = document.createElement("div");
    left.className = "doc-file-left";

    const icon = document.createElement("div");
    icon.className = "doc-file-icon";
    icon.innerHTML = '<i class="fa-solid fa-file"></i>';

    const info = document.createElement("div");
    info.className = "doc-file-info";
    const name = document.createElement("h5");
    name.textContent = file.name;
    const size = document.createElement("span");
    size.textContent = `${(file.size / 1024).toFixed(1)} KB`;
    info.append(name, size);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "doc-remove";
    remove.setAttribute("aria-label", `Remove ${file.name}`);
    remove.innerHTML = '<i class="fa-solid fa-trash"></i>';

    left.append(icon, info);
    item.append(left, remove);
    fileList.appendChild(item);
  });
};

const scrollDashboardCardToTop = (element) => {
  const target = element?.closest(".content-card")
    || element?.closest(".beni-tab-content")
    || element;

  window.requestAnimationFrame(() => {
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
};

const arabicStatus = (value) => ({
  "in progress": "قيد التنفيذ", submitted: "تم التقديم", "under review": "قيد المراجعة",
  approved: "تمت الموافقة", rejected: "مرفوض", completed: "مكتمل",
}[String(value || "").trim().toLowerCase()] || value);

const getRequestType = (request) => request.requestType || request.request_type || "Aid Request";
const getRequestDate = (request) => request.savedDate || request.submittedDate || request.insertdate || request.createdDate || request.date || "";
const getRequestAmount = (request) => request.requestedAmount ?? request.requested_amount ?? request.amount ?? request.requestAmount;
const formatRequestAmount = (request, language) => {
  const amount = Number(getRequestAmount(request));
  if (!Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat(language === "ar" ? "ar-AE" : "en-AE", {
    style: "currency",
    currency: "AED",
    minimumFractionDigits: 2,
  }).format(amount);
};
const getRequestCustomerStatusKey = (request) => {
  if (request?.isDraft) return "draft";
  const status = [request?.statusCode, request?.status, request?.status_ar]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
  if (/\bEXPIRED\b/.test(status) || /منتهي(?:ة)?(?: الصلاحية)?/.test(status)) return "expired";
  if (/CANCELLED|CANCELED|REJECT/.test(status) || /مرفوض|ملغى|ملغي|إلغاء/.test(status)) return "rejected";
  if (/ARCHIVED|COMPLETED|CLOSED/.test(status) || /مؤرشف|مكتمل|مكتملة|مغلق|مغلقة/.test(status)) return "completed";
  return "under-process";
};

const getRequestCustomerStatus = (request, isArabic) => ({
  draft: isArabic ? "مسودة" : "Draft",
  expired: isArabic ? "منتهي الصلاحية" : "Expired",
  rejected: isArabic ? "مرفوض" : "Rejected",
  completed: isArabic ? "مكتمل" : "Completed",
  "under-process": isArabic ? "قيد المعالجة" : "Under Process",
}[getRequestCustomerStatusKey(request)]);

const getRequestStatusClass = (request) => ({
  draft: "draft",
  expired: "expired",
  rejected: "rejected",
  completed: "completed",
  "under-process": "progress",
}[getRequestCustomerStatusKey(request)]);

const getRequestTime = (request) => {
  const value = Date.parse(getRequestDate(request));
  return Number.isNaN(value) ? 0 : value;
};

export default function BeneficentDashboard({ language = "en" }) {
  const isArabic = language === "ar";
  const dashboardRef = useRef(null);
  const interactions = useHomeInteractions();
  const [isUaePass, setIsUaePass] = useState(() =>
    String(getStoredUser()?.profile?.regfrom || "").toLowerCase() === "uaepass"
  );
  const [showUaePassNotice, setShowUaePassNotice] = useState(false);
  const [dashboardData, setDashboardData] = useState({ profile: {}, eligibility: {}, totalRequests: 0, activeRequests: 0, completedRequests: 0, latestRequest: null, recentActivity: [], notifications: [], requests: [], drafts: [] });
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [requestSearch, setRequestSearch] = useState("");
  const [requestStatus, setRequestStatus] = useState("all");
  const [requestType, setRequestType] = useState("all");
  const [requestSort, setRequestSort] = useState("newest");
  const [requirementUploads, setRequirementUploads] = useState({});
  const [requestActionMessages, setRequestActionMessages] = useState({});
  const [aiDocumentModal, setAiDocumentModal] = useState(null);
  const [chatRequest, setChatRequest] = useState(null);
  const [chatUnreadCounts, setChatUnreadCounts] = useState({});
  const [trackingRequestNo, setTrackingRequestNo] = useState("");
  const [trackingRequest, setTrackingRequest] = useState(null);
  const [trackingMessage, setTrackingMessage] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [aidWorkspaceView, setAidWorkspaceView] = useState("requests");

  const aidRequestItems = useMemo(() => [...(dashboardData.drafts || []), ...dashboardData.requests], [dashboardData.drafts, dashboardData.requests]);
  const requestStatuses = useMemo(() => [...new Set(aidRequestItems.map(getRequestCustomerStatusKey))], [aidRequestItems]);
  const requestTypes = useMemo(() => [...new Set(aidRequestItems.map(getRequestType).filter(Boolean))], [aidRequestItems]);
  const filteredRequests = useMemo(() => {
    const query = requestSearch.trim().toLowerCase();
    return aidRequestItems
      .filter((request) => {
        const matchesSearch = !query || [
          request.requestno,
          request.customerRequestNumber,
          request.internalRequestNumber,
          getRequestType(request),
          request.status,
          getRequestCustomerStatus(request, isArabic),
          getRequestDate(request),
          getRequestAmount(request),
          request.description,
        ].some((value) => String(value || "").toLowerCase().includes(query));
        return matchesSearch
          && (requestStatus === "all" || getRequestCustomerStatusKey(request) === requestStatus)
          && (requestType === "all" || getRequestType(request) === requestType);
      })
      .sort((first, second) => requestSort === "oldest"
        ? getRequestTime(first) - getRequestTime(second)
        : getRequestTime(second) - getRequestTime(first));
  }, [aidRequestItems, isArabic, requestSearch, requestSort, requestStatus, requestType]);

  const clearRequestFilters = () => {
    setRequestSearch("");
    setRequestStatus("all");
    setRequestType("all");
    setRequestSort("newest");
  };

  const trackAidRequest = async (event) => {
    event.preventDefault();
    const requestNo = trackingRequestNo.trim();
    if (!requestNo) {
      setTrackingRequest(null);
      setTrackingMessage(isArabic ? "يرجى إدخال رقم الطلب." : "Please enter your request number.");
      return;
    }

    setTrackingLoading(true);
    setTrackingMessage("");
    setTrackingRequest(null);
    try {
      const token = getStoredToken();
      const response = await fetch(getApiUrl(`/api/GetAidRequestStatus.ashx?requestno=${encodeURIComponent(requestNo)}`), {
        credentials: "include",
        headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (response.status === 401) {
        redirectToLogin();
        return;
      }
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || (isArabic ? "تعذر استرجاع حالة الطلب." : "Unable to retrieve the request status."));
      const dashboardRequest = dashboardData.requests.find((request) => [
        request.requestno,
        request.customerRequestNumber,
        request.internalRequestNumber,
      ].some((value) => String(value || "").trim().toLowerCase() === requestNo.toLowerCase()));
      setTrackingRequest(getRequestCustomerStatusKey(dashboardRequest) === "expired" ? {
        ...result.request,
        statusCode: "EXPIRED",
        status: "Expired",
        status_ar: "منتهي الصلاحية",
      } : result.request);
    } catch (error) {
      setTrackingMessage(error.message || (isArabic ? "تعذر استرجاع حالة الطلب." : "Unable to retrieve the request status."));
    } finally {
      setTrackingLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const refreshUnreadChats = async () => {
      try {
        const token = getStoredToken();
        if (!token) return;
        const response = await fetch(getApiUrl("/api/RequestChat.ashx?unread=1"), {
          cache: "no-store", credentials: "include",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) return;
        if (active) setChatUnreadCounts(Object.fromEntries((result.data || []).map((item) => [String(item.requestId), Number(item.count) || 0])));
      } catch { /* The request list remains usable while chat counts retry. */ }
    };
    refreshUnreadChats();
    const timer = window.setInterval(refreshUnreadChats, 15000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  const uploadRequestedDocument = async (request, requirement, file) => {
    if (!file) return;
    const uploadKey = `${request.id}-${requirement.id}`;
    setRequirementUploads((current) => ({ ...current, [uploadKey]: true }));
    setRequestActionMessages((current) => ({ ...current, [request.id]: "" }));
    const documentTitle = requirement.title_en || "Requested Document";
    const displayTitle = (isArabic ? requirement.title_ar : requirement.title_en) || documentTitle;
    setAiDocumentModal({ state: "loading", title: displayTitle });
    try {
      const token = getStoredToken();
      const validationData = new FormData();
      validationData.append("document", file, file.name);
      validationData.append("documentType", documentTitle);
      validationData.append("documentTitleAr", requirement.title_ar || "");
      validationData.append("language", isArabic ? "ar" : "en");
      validationData.append("validationOnly", "true");
      const validationResponse = await fetch(getApiUrl("/api/AidDocumentValidation.ashx"), {
        method: "POST", credentials: "include", body: validationData,
        headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const validation = await validationResponse.json().catch(() => ({}));
      if (!validationResponse.ok || !validation.success) throw new Error(validation.message || "Unable to verify this document.");
      const validationStatus = ["accepted", "review", "rejected"].includes(String(validation.status).toLowerCase()) ? String(validation.status).toLowerCase() : "review";
      const verificationResult = { state: validationStatus, title: displayTitle, outcomeCode: validation.outcomeCode, customerMessage: validation.customerMessage };
      if (validationStatus === "rejected") {
        setAiDocumentModal(verificationResult);
        setRequestActionMessages((current) => ({ ...current, [request.id]: isArabic ? "رفض التحقق بالذكاء الاصطناعي المستند. يرجى رفع نسخة واضحة وصالحة." : "AI verification rejected the document. Upload a clear and valid replacement." }));
        return;
      }
      const formData = new FormData();
      formData.append("requestId", String(request.id));
      formData.append("requirementId", String(requirement.id));
      formData.append("file", file);
      formData.append("aiScore", String(Number(validation.score) || 0));
      formData.append("aiStatus", validationStatus);
      formData.append("aiValidity", validation.validity || (validationStatus === "accepted" ? "valid" : "review"));
      formData.append("aiChecks", JSON.stringify(Array.isArray(validation.checks) ? validation.checks : []));
      formData.append("aiNotice", validation.notice || "");
      const response = await fetch(getApiUrl("/api/ApplicantRequiredDocumentUpload.ashx"), {
        method: "POST", credentials: "include", body: formData,
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to upload the replacement document.");
      setDashboardData((current) => ({ ...current, requests: current.requests.map((item) => item.id === request.id ? {
        ...item,
        actionRequired: (item.actionRequired || []).map((entry) => entry.id === requirement.id ? { ...entry, status: "RECEIVED", aiStatus: validationStatus, aiScore: Number(validation.score) || 0 } : entry),
      } : item) }));
      setAiDocumentModal(verificationResult);
      setRequestActionMessages((current) => ({ ...current, [request.id]: isArabic ? "تم التحقق من المستند ورفعه. ارفع جميع المستندات المطلوبة ثم أرسل التحديثات." : "Document checked by AI and uploaded. Upload every requested document, then submit the updates." }));
    } catch (uploadError) {
      setAiDocumentModal({ state: "review", title: displayTitle, score: 0, checks: [], notice: uploadError.message || "Unable to verify this document." });
      setRequestActionMessages((current) => ({ ...current, [request.id]: uploadError.message || "Unable to upload the replacement document." }));
    } finally {
      setRequirementUploads((current) => ({ ...current, [uploadKey]: false }));
    }
  };

  const submitApplicantUpdates = async (request) => {
    setRequestActionMessages((current) => ({ ...current, [request.id]: "" }));
    try {
      const token = getStoredToken();
      const response = await fetch(getApiUrl("/api/ApplicantSubmitUpdates.ashx"), {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ requestId: request.id }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to submit the application updates.");
      setDashboardData((current) => ({ ...current, requests: current.requests.map((item) => item.id === request.id ? {
        ...item, status: isArabic ? "أعيد تقديمه" : "Resubmitted", status_ar: "أعيد تقديمه", statusCode: "RESUBMITTED", actionRequired: [],
      } : item) }));
      setRequestActionMessages((current) => ({ ...current, [request.id]: isArabic ? "تم إرسال التحديثات وإعادة الطلب إلى الموظف المعين للمراجعة." : "Updates submitted and returned to the assigned employee for review." }));
    } catch (submitError) {
      setRequestActionMessages((current) => ({ ...current, [request.id]: submitError.message || "Unable to submit the application updates." }));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const loadDashboard = async () => {
      try {
        setDashboardLoading(true);
        const token = getStoredToken();
        const response = await fetch(getApiUrl("/api/BeneficiaryDashboard.ashx"), {
          credentials: "include",
          signal: controller.signal,
          headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (response.status === 401) {
          redirectToLogin();
          return;
        }
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) throw new Error(result.message || "Unable to load dashboard data.");
        const requests = (result.requests || []).map((request) => {
          const customerRequestNumber = getCustomerRequestNumber(request);
          const normalized = { ...request, requestno: customerRequestNumber };
          return isArabic ? {
            ...normalized,
            status: request.status_ar || arabicStatus(request.status),
            requestType: request.request_type_ar || request.requestType_ar || request.requestType || "طلب مساعدة",
          } : normalized;
        });
        const drafts = (result.drafts || []).map((draft) => isArabic ? {
          ...draft,
          // Draft status is fixed UI copy. Do not display the legacy API's
          // status_ar value because some saved records contain mojibake.
          status: "مسودة",
          requestType: draft.requestType_ar || draft.requestType || "طلب مساعدة",
        } : draft);
        const profile = isArabic ? { ...result.profile, fullname: result.profile?.fullname_ar || result.profile?.fullname, address: result.profile?.address_ar || result.profile?.address } : (result.profile || {});
        setDashboardData({
          profile,
          eligibility: result.eligibility || {},
          totalRequests: Number(result.totalRequests) || 0,
          activeRequests: Number(result.activeRequests) || 0,
          completedRequests: Number(result.completedRequests) || 0,
          latestRequest: result.latestRequest || requests[0] || null,
          recentActivity: Array.isArray(result.recentActivity) ? result.recentActivity : [],
          notifications: Array.isArray(result.notifications) ? result.notifications : [],
          requests,
          drafts,
        });
        setIsUaePass(Boolean(profile.isVerified));
        setDashboardError("");
      } catch (error) {
        if (error.name !== "AbortError") setDashboardError(error.message || "Unable to load dashboard data.");
      } finally {
        if (!controller.signal.aborted) setDashboardLoading(false);
      }
    };
    loadDashboard();
    return () => controller.abort();
  }, [isArabic]);

  useEffect(() => {
    document.title = isArabic ? "لوحة تحكم المستفيد | مؤسسة تراحم الخيرية" : "Beneficiary Dashboard | Tarahum Charity Foundation";
    if (isArabic) {
      document.documentElement.lang = "ar";
      document.documentElement.dir = "rtl";
      document.body.classList.add("arabic-layout-page");
    }
    const root = dashboardRef.current;
    if (!root) return undefined;

    const dashboardParams = new URLSearchParams(window.location.search);
    const requestedTab = dashboardParams.get("appointment") ? "appointment-tab" : dashboardParams.get("tab");
    const requestedRestrictedTab = requestedTab
      && !UNVERIFIED_ACCESSIBLE_TABS.has(requestedTab)
      && !isUaePass;
    if (requestedRestrictedTab) {
      activateDashboardTab(root, "dashboard-tab");
      setShowUaePassNotice(true);
    } else if (requestedTab === "tracking-tab" || requestedTab === "history-tab") {
      setAidWorkspaceView(requestedTab === "tracking-tab" ? "tracking" : "history");
      activateDashboardTab(root, "Aid-Requests");
    } else if (requestedTab) {
      activateDashboardTab(root, requestedTab);
    }

    const preloaderTimer = window.setTimeout(() => {
      document.querySelector(".preloader")?.classList.add("policy-loader-hide");
    }, 900);

    const handleClick = (event) => {
      const mobileToggle = event.target.closest(".beni-mobile-menu-toggle");
      if (mobileToggle) {
        const layout = root.querySelector(".benifi-dash-bord");
        setMobileMenuOpen(root, !layout?.classList.contains("beni-menu-open"));
        return;
      }

      if (event.target.closest(".beni-mobile-menu-backdrop")) {
        setMobileMenuOpen(root, false);
        return;
      }

      // Dashboard tabs are handled in place, but real sidebar links must keep
      // their normal navigation. The In-Kind Assistance link performs its own
      // UAE Pass access check before loading the separate route.
      const tabControl = event.target.closest(".beni-menu:not(a[href]), [data-target-tab]");
      if (tabControl) {
        event.preventDefault();
        const targetTab = tabControl.dataset.tab || tabControl.dataset.targetTab;
        const isRestricted = !isUaePass && (
          (targetTab && !UNVERIFIED_ACCESSIBLE_TABS.has(targetTab))
          || (tabControl.classList.contains("beni-menu") && !targetTab)
        );
        if (isRestricted) {
          event.stopPropagation();
          setShowUaePassNotice(true);
          setMobileMenuOpen(root, false);
          return;
        }
        activateDashboardTab(
          root,
          targetTab
        );
        setMobileMenuOpen(root, false);
        return;
      }

      const browseButton = event.target.closest("#browseBtn");
      if (browseButton) {
        event.preventDefault();
        root.querySelector("#docUpload")?.click();
        return;
      }

      const removeButton = event.target.closest(".doc-remove");
      if (removeButton) {
        event.preventDefault();
        removeButton.closest(".doc-file-item")?.remove();
        return;
      }

      const submitButton = event.target.closest(".submit-btn");
      if (submitButton && !submitButton.closest("form")) {
        scrollDashboardCardToTop(submitButton);
      }

      const placeholderLink = event.target.closest('a[href="#"], a[href=""]');
      if (placeholderLink) event.preventDefault();
    };

    const handleChange = (event) => {
      if (event.target.matches("#docUpload")) {
        appendUploadedFiles(root, event.target.files);
        event.target.value = "";
      }
    };

    const handleDragOver = (event) => {
      const dropArea = event.target.closest("#dropArea");
      if (!dropArea) return;
      event.preventDefault();
      dropArea.classList.add("active");
    };

    const handleDragLeave = (event) => {
      const dropArea = event.target.closest("#dropArea");
      if (dropArea && !dropArea.contains(event.relatedTarget)) {
        dropArea.classList.remove("active");
      }
    };

    const handleDrop = (event) => {
      const dropArea = event.target.closest("#dropArea");
      if (!dropArea) return;
      event.preventDefault();
      dropArea.classList.remove("active");
      appendUploadedFiles(root, event.dataTransfer?.files);
    };

    root.addEventListener("click", handleClick);
    root.addEventListener("change", handleChange);
    root.addEventListener("dragover", handleDragOver);
    root.addEventListener("dragleave", handleDragLeave);
    root.addEventListener("drop", handleDrop);

    return () => {
      window.clearTimeout(preloaderTimer);
      if (isArabic) {
        document.documentElement.lang = "en";
        document.documentElement.dir = "ltr";
        document.body.classList.remove("arabic-layout-page");
      }
      root.removeEventListener("click", handleClick);
      root.removeEventListener("change", handleChange);
      root.removeEventListener("dragover", handleDragOver);
      root.removeEventListener("dragleave", handleDragLeave);
      root.removeEventListener("drop", handleDrop);
    };
  }, [isArabic, isUaePass]);

  const aiModalDetails = getCustomerDocumentOutcome(aiDocumentModal, isArabic) || (aiDocumentModal ? ({
    accepted: { title: isArabic ? "تم قبول المستند" : "Document Accepted", message: isArabic ? "اكتمل التحقق بالذكاء الاصطناعي بنجاح." : "AI verification completed successfully.", notice: isArabic ? "المستند يستوفي المتطلبات وتم قبوله." : "The document meets the requested requirements and has been accepted.", button: isArabic ? "متابعة" : "Great! Continue" },
    review: { title: isArabic ? "قيد المراجعة" : "Under Review", message: isArabic ? "عالج الذكاء الاصطناعي المستند، وسيقوم فريقنا بمراجعته." : "AI processed the document. Our team will review it shortly.", notice: isArabic ? "تم رفع المستند، لكنه يحتاج إلى مراجعة إضافية." : "The document was uploaded but requires additional review.", button: isArabic ? "حسنًا" : "OK, Got it" },
    rejected: { title: isArabic ? "تم رفض المستند" : "Document Rejected", message: isArabic ? "وجد التحقق بالذكاء الاصطناعي مشكلات في هذا المستند." : "AI verification found issues with this document.", notice: isArabic ? "يرجى رفع مستند جديد واضح وكامل وصالح." : "Upload a new document that is clear, complete, and valid.", button: isArabic ? "إغلاق" : "Close" },
  }[aiDocumentModal.state] || null) : null);
  const documentLoadingCopy = getDocumentLoadingCopy(aiDocumentModal?.title, isArabic);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}
      <main ref={dashboardRef} className={isArabic ? "arabic-beneficiary-dashboard" : ""} dir={isArabic ? "rtl" : undefined}>
      {showUaePassNotice ? (
        <div className="beneficiary-uae-pass-notice" role="alert" aria-live="assertive">
          <div className="beneficiary-uae-pass-notice-icon" aria-hidden="true">
            <i className="fa-solid fa-shield-halved"></i>
          </div>
          <div className="beneficiary-uae-pass-notice-copy">
            <strong>{isArabic ? "يرجى الاتصال بالهوية الرقمية UAE Pass" : "Please connect with UAE Pass"}</strong>
            <span>{isArabic ? "يجب توثيق حسابك للوصول إلى هذا القسم." : "Verify your account to access this section."}</span>
          </div>
          <button type="button" onClick={() => setShowUaePassNotice(false)} aria-label={isArabic ? "إغلاق الرسالة" : "Close message"}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      ) : null}
      <section className="benifi-dash-bord-wrap">
        <div className="container">
          <div className="benifi-dash-bord">
            <button className="beni-mobile-menu-toggle" type="button" aria-expanded="false" aria-controls="beneficentSidebar">
              <i className="fa-solid fa-bars"></i>
{isArabic ? "قائمة المستفيد" : "Beneficiary Menu"}
            </button>
            <div className="beni-mobile-menu-backdrop"></div>
            <aside className="beni-sidebar" id="beneficentSidebar">
              <BeneficiarySidebarProfile onVerificationChange={setIsUaePass} language={language} />
              <div className="beni-menu-wrap">
                <button className="beni-menu active" data-tab="dashboard-tab">
                  <i className="fa-solid fa-house"></i>
{isArabic ? "لوحة التحكم" : "Dashboard"}
                </button>
                <button className="beni-menu" data-tab="family-tab">
                  <i className="fa-solid fa-users"></i>
{isArabic ? "بيانات الأسرة" : "Family Details"}
                </button>
                <button className="beni-menu" data-tab="profile-tab">
                  <i className="fa-regular fa-user"></i>
{isArabic ? "الملف الشخصي" : "Profile"}
                </button>
                <button className="beni-menu" data-tab="financial-tab">
                  <i className="fa-solid fa-wallet"></i>
{isArabic ? "المعلومات المالية" : "Financial Info"}
                </button>
                <button className="beni-menu" data-tab="Aid-Requests" onClick={() => setAidWorkspaceView("requests")}>
                  <i className="fa-solid fa-hand-holding-heart"></i>
{isArabic ? "طلبات المساعدة" : "Aid Requests"}
                </button>
                <Link
                  className="beni-menu"
                  to={isArabic ? "/ar/in-kind-assistance" : "/in-kind-assistance"}
                  reloadDocument
                >
                  <i className="fa-solid fa-box-open"></i>
                  {isArabic ? "المساعدة العينية" : "In-Kind Assistance"}
                </Link>
                <button className="beni-menu" data-tab="appointment-tab">
                  <i className="fa-regular fa-calendar"></i>
{isArabic ? "المواعيد" : "Appointments"}
                </button>
                <button className="beni-menu" data-tab="Settings-tab">
                  <i className="fa-solid fa-sliders"></i>
{isArabic ? "الإعدادات" : "Settings"}
                </button>
              </div>
            </aside>
            <div className="beni-main">
              <div className="beni-tab-content active" id="dashboard-tab">
                <div className="beneficiary-dashboard">
                  <div className="beneficiary-topbar">
                    <div className="beneficiary-title">
                      <h2>{(isArabic ? dashboardData.profile.fullname_ar : null) || dashboardData.profile.fullname || (isArabic ? "المستفيد" : "Beneficiary")}</h2>
                      <p>{isArabic ? "إليك آخر المستجدات المتعلقة بطلباتك اليوم." : "Here’s what’s happening with your requests today."}</p>
                    </div>
                    <div className="beneficiary-actions">
                      <a href="" data-target-tab="notifications-tab">
                        <div className="beneficiary-action-btn">
                          <i className="fa-solid fa-bell"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                  <AssistanceRequestNotice
                    language={language}
                    dashboard
                    onViewRequests={() => setAidWorkspaceView("requests")}
                  />
                  <div className="beneficiary-main-grid">
                    <div className="beneficiary-left">
                      <div className="track-bn-extra">
                        <div className="track-bn-box">
                          <div className="track-bn-cards">
                            <div className="track-bn-card">
                              <div className="track-bn-card-icon track-clr-blue">
                                <i className="fa-solid fa-user-check"></i>
                              </div>
                              <span>{isArabic ? "اكتمال الملف الشخصي" : "Profile Completion"}</span>
                              <h4>{dashboardData.profile.completion || 0}%</h4>
                              <p>{isArabic ? "استناداً إلى تفاصيل ملف المستفيد المحفوظة." : "Based on your saved beneficiary profile details."}</p>
                            </div>
                            <div className="track-bn-card">
                              <div className="track-bn-card-icon track-clr-orange">
                                <i className="fa-solid fa-file-circle-plus"></i>
                              </div>
                              <span>{isArabic ? "الطلبات النشطة" : "Active Requests"}</span>
                              <h4>{dashboardData.activeRequests}</h4>
                              <p>{isArabic ? "طلبات قيد المعالجة حالياً." : "Requests currently under processing."}</p>
                            </div>
                            <div className="track-bn-card">
                              <div className="track-bn-card-icon track-clr-purple">
                                <i className="fa-solid fa-hand-holding-heart"></i>
                              </div>
                              <span>{isArabic ? "الطلبات المكتملة" : "Completed Requests"}</span>
                              <h4>{dashboardData.completedRequests}</h4>
                              <p>{isArabic ? "طلبات تم إغلاقها في النظام." : "Requests marked closed in the system."}</p>
                            </div>
                            <div className="track-bn-card">
                              <div className="track-bn-card-icon track-clr-blue">
                                <i className="fa-regular fa-calendar-check"></i>
                              </div>
                              <span>{isArabic ? "إجمالي الطلبات" : "Total Requests"}</span>
                              <h4>{dashboardData.totalRequests}</h4>
                              <p>{isArabic ? "إجمالي طلبات المساعدة المقدمة من حسابك." : "Total aid requests submitted from your account."}</p>
                            </div>
                            <div className="track-bn-card">
                              <div className="track-bn-card-icon track-clr-green">
                                <i className="fa-solid fa-folder-open"></i>
                              </div>
                              <span>{isArabic ? "أحدث حالة" : "Latest Status"}</span>
                              <h4>{dashboardData.latestRequest ? getRequestCustomerStatus(dashboardData.latestRequest, isArabic) : (isArabic ? "لا توجد طلبات" : "No requests")}</h4>
                              <p>{dashboardData.latestRequest?.requestno ? (isArabic ? `طلب ${dashboardData.latestRequest.requestno}` : `Request ${dashboardData.latestRequest.requestno}`) : (isArabic ? "لا يوجد طلب مسجل في قاعدة البيانات." : "No application is recorded in the database.")}</p>
                            </div>
                          </div>
                        </div>
                        <div className="track-bn-box">
                          <div className="track-bn-head">
                            <h3>{isArabic ? "إجراءات سريعة" : "Quick Actions"}</h3>
                          </div>
                          <div className="track-bn-actions">
                            <button className="track-bn-action-btn" data-target-tab="Aid-Requests" onClick={() => setAidWorkspaceView("requests")}>
                              <i className="fa-solid fa-hand-holding-heart"></i>
{isArabic ? "طلب مساعدة" : "Request Assistance"}
                            </button>
                            <button className="track-bn-action-btn" data-target-tab="financial-tab">
                              <i className="fa-solid fa-wallet"></i>
{isArabic ? "تحديث المعلومات المالية" : "Update Financial Info"}
                            </button>
                            <button className="track-bn-action-btn" type="button" data-target-tab="appointment-tab">
                              <i className="fa-regular fa-calendar-check"></i>
{isArabic ? "حجز موعد" : "Book Appointment"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="beneficiary-sidebar">
                      <div className="profile-card">
                        <div className="profile-top">
                          <img
                            src={getCloudflareImageUrl(dashboardData.profile.ImagePath, "w=240,h=240,fit=cover,sharpen=1") || "/images/profile.png"}
                            alt="Beneficiary profile"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = "/images/profile.png";
                            }}
                          />
                          <div>
                            <h3>{dashboardData.profile.fullname || (isArabic ? "المستفيد" : "Beneficiary")}</h3>
                            <p>{dashboardData.profile.accounttype || (isArabic ? "حساب المستفيد" : "Beneficiary Account")}</p>
                            <span className={`verify-tag${isUaePass ? "" : " unverified"}`}>
                              {isUaePass ? (isArabic ? "مستفيد موثق" : "Verified Beneficiary") : (isArabic ? "مستفيد غير موثق" : "Unverified Beneficiary")}
                            </span>
                          </div>
                        </div>
                        <div className="profile-info">
                          <div className="profile-info-item">
                            <i className="fa-solid fa-location-dot"></i>
{dashboardData.profile.address || (isArabic ? "العنوان غير متوفر" : "Address not provided")}
                          </div>
                          <div className="profile-info-item">
                            <i className="fa-solid fa-phone"></i>
{dashboardData.profile.mobileno || (isArabic ? "رقم الهاتف غير متوفر" : "Mobile not provided")}
                          </div>
                          <div className="profile-info-item">
                            <i className="fa-solid fa-envelope"></i>
{dashboardData.profile.email || (isArabic ? "البريد الإلكتروني غير متوفر" : "Email not provided")}
                          </div>
                        </div>
                        <a href="#" data-target-tab="profile-tab" className="profile-btn">
{isArabic ? "عرض الملف الشخصي" : "View Profile"}
                          <i className="fa-solid fa-arrow-right"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="beni-tab-content" id="family-tab">
                <div className="content-card">
                  <div className="card-title">
                    <h2>{isArabic ? "بيانات أفراد الأسرة" : "Family Member Details"}</h2>
                    <p>{isArabic ? "إدارة بيانات الأسرة والعمل والدخل والتحقق." : "Manage household, employment, income, and verification details."}</p>
                  </div>
                  <BeneficiaryFamilyDetailsForm language={language} />
                </div>
              </div>
              <div className="beni-tab-content" id="profile-tab">
                <BeneficiaryProfileForm language={language} />
              </div>
              <div className="beni-tab-content" id="financial-tab">
                <div className="content-card">
                  <div className="card-title">
                    <h2>{isArabic ? "المعلومات المالية" : "Financial Information"}</h2>
                    <p>{isArabic ? "تقييم الأهلية المالية." : "Evaluates financial eligibility."}</p>
                  </div>
                  <BeneficiaryFinancialInfoForm language={language} />
                </div>
              </div>
              <div className="beni-tab-content" id="request-tab">
                <div className="content-card">
                  <div className="card-title">
                    <h2>New Assistance Request</h2>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Request Type</label>
                      <select>
                        <option>Financial Aid</option>
                        <option>Medical Aid</option>
                        <option>Education Support</option>
                        <option>Food Assistance</option>
                        <option>Housing Support</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Requested Amount</label>
                      <input type="text" placeholder="AED 5,000" />
                    </div>
                    <div className="form-group">
                      <label>Urgency Level</label>
                      <select>
                        <option>High</option>
                        <option>Medium</option>
                        <option>Low</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Preferred Contact</label>
                      <select>
                        <option>Phone</option>
                        <option>Email</option>
                        <option>SMS</option>
                      </select>
                    </div>
                    <div className="form-group full-width">
                      <label>Request Description</label>
                      <textarea>Need urgent medical assistance for surgery treatment.</textarea>
                    </div>
                    <div className="form-group full-width">
                      <label>Upload Supporting Documents</label>
                      <input type="file" />
                    </div>
                  </div>
                  <button className="submit-btn">Submit Request</button>
                </div>
              </div>
              <div className="beni-tab-content" id="Aid-Requests">
                <div className="content-card">
                  <div className="card-title">
                    <h2>{isArabic ? "طلبات المساعدة" : "Aid Requests"}</h2>
                    <p>{isArabic ? "عرض الطلبات وتتبع حالتها ومراجعة السجل في مكان واحد." : "View requests, track an application, and review aid history in one place."}</p>
                  </div>
                  <div className="aid-workspace-tabs" role="tablist" aria-label={isArabic ? "أقسام طلبات المساعدة" : "Aid request sections"}>
                    <button type="button" role="tab" aria-selected={aidWorkspaceView === "requests"} className={aidWorkspaceView === "requests" ? "active" : ""} onClick={() => setAidWorkspaceView("requests")}><i className="fa-solid fa-hand-holding-heart"></i>{isArabic ? "طلبات المساعدة" : "Aid Requests"}</button>
                    <button type="button" role="tab" aria-selected={aidWorkspaceView === "tracking"} className={aidWorkspaceView === "tracking" ? "active" : ""} onClick={() => setAidWorkspaceView("tracking")}><i className="fa-solid fa-chart-line"></i>{isArabic ? "تتبع الطلب" : "Application Tracking"}</button>
                    <button type="button" role="tab" aria-selected={aidWorkspaceView === "history"} className={aidWorkspaceView === "history" ? "active" : ""} onClick={() => setAidWorkspaceView("history")}><i className="fa-solid fa-clock-rotate-left"></i>{isArabic ? "سجل المساعدات" : "Aid History"}</button>
                  </div>
                  {aidWorkspaceView === "requests" ? (
                  <div className="bn-profile-wrap">
                    <div className="bn-profile-box">
                      <div className="bn-profile-section aid-requests-list-section">
                        <div className="aid-request-filters" role="search" aria-label={isArabic ? "تصفية طلبات المساعدة" : "Filter aid requests"}>
                          <label className="aid-request-search">
                            <span className="sr-only">{isArabic ? "البحث في الطلبات" : "Search requests"}</span>
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <input type="search" value={requestSearch} onChange={(event) => setRequestSearch(event.target.value)} placeholder={isArabic ? "ابحث برقم الطلب أو النوع..." : "Search by request number or type..."} />
                          </label>
                          <label>
                            <span>{isArabic ? "الحالة" : "Status"}</span>
                            <select value={requestStatus} onChange={(event) => setRequestStatus(event.target.value)}>
                              <option value="all">{isArabic ? "كل الحالات" : "All statuses"}</option>
                              {requestStatuses.map((status) => <option value={status} key={status}>{getRequestCustomerStatus({ isDraft: status === "draft", statusCode: status }, isArabic)}</option>)}
                            </select>
                          </label>
                          <label>
                            <span>{isArabic ? "نوع المساعدة" : "Aid type"}</span>
                            <select value={requestType} onChange={(event) => setRequestType(event.target.value)}>
                              <option value="all">{isArabic ? "كل الأنواع" : "All types"}</option>
                              {requestTypes.map((type) => <option value={type} key={type}>{type}</option>)}
                            </select>
                          </label>
                          <label>
                            <span>{isArabic ? "الترتيب" : "Sort"}</span>
                            <select value={requestSort} onChange={(event) => setRequestSort(event.target.value)}>
                              <option value="newest">{isArabic ? "الأحدث أولاً" : "Newest first"}</option>
                              <option value="oldest">{isArabic ? "الأقدم أولاً" : "Oldest first"}</option>
                            </select>
                          </label>
                        </div>
                        <div className="aid-request-results-bar">
                          <p>{isArabic ? `عرض ${filteredRequests.length} من ${aidRequestItems.length} طلب` : `Showing ${filteredRequests.length} of ${aidRequestItems.length} requests`}</p>
                          {(requestSearch || requestStatus !== "all" || requestType !== "all" || requestSort !== "newest") ? (
                            <button type="button" onClick={clearRequestFilters}><i className="fa-solid fa-rotate-left"></i>{isArabic ? "مسح عوامل التصفية" : "Clear filters"}</button>
                          ) : null}
                        </div>
                        {dashboardLoading ? (
                          <div className="aid-request-empty"><i className="fa-solid fa-spinner fa-spin"></i><h5>{isArabic ? "جارٍ تحميل الطلبات..." : "Loading requests..."}</h5></div>
                        ) : filteredRequests.length ? (
                          <div className="aid-request-list">
                            {filteredRequests.map((request, index) => (
                              <article className={`aid-request-list-card${request.isDraft ? " is-draft" : ""}`} key={request.contextKey || request.requestno || `request-${index}`}>
                                <div className="aid-request-list-card-top">
                                  <div className="aid-request-number"><span>{request.isDraft ? (isArabic ? "طلب محفوظ" : "Saved application") : (isArabic ? "رقم الطلب" : "Request number")}</span><h5>{request.isDraft ? (isArabic ? "مسودة طلب مساعدة" : "Aid application draft") : (request.requestno || (isArabic ? "غير متوفر" : "Not available"))}</h5></div>
                                  <span className={`aid-request-status ${getRequestStatusClass(request)}`}>{getRequestCustomerStatus(request, isArabic)}</span>
                                </div>
                                <div className="aid-request-list-details">
                                  <div><i className="fa-solid fa-hand-holding-heart"></i><span>{isArabic ? "نوع المساعدة" : "Aid type"}</span><strong>{getRequestType(request)}</strong></div>
                                  <div><i className="fa-regular fa-calendar"></i><span>{request.isDraft ? (isArabic ? "آخر حفظ" : "Last saved") : (isArabic ? "تاريخ التقديم" : "Submitted date")}</span><strong>{getRequestDate(request) || "—"}</strong></div>
                                  <div><i className="fa-solid fa-money-bill-wave"></i><span>{isArabic ? "المبلغ المطلوب" : "Requested amount"}</span><strong>{formatRequestAmount(request, language)}</strong></div>
                                </div>
                                {!request.isDraft && (request.actionRequired || []).length ? (
                                  <section className="beneficiary-action-required" aria-labelledby={`action-required-${request.id}`}>
                                    <div className="beneficiary-action-required-head"><div><span>{isArabic ? "إجراء مطلوب" : "Action Required"}</span><h5 id={`action-required-${request.id}`}>{isArabic ? "أعد رفع المستندات التي حددها الموظف" : "Re-upload the documents selected by the employee"}</h5></div><i className="fa-solid fa-triangle-exclamation"></i></div>
                                    <div className="beneficiary-required-documents">
                                      {(request.actionRequired || []).map((requirement) => {
                                        const uploadKey = `${request.id}-${requirement.id}`;
                                        const received = String(requirement.status).toUpperCase() === "RECEIVED";
                                        return <article key={requirement.id}><div><strong>{(isArabic ? requirement.title_ar : requirement.title_en) || requirement.title_en}</strong><small>{isArabic ? `الموعد النهائي: ${requirement.dueDate || "—"}` : `Due: ${requirement.dueDate || "—"}`}</small></div><span className={received ? "received" : "requested"}>{received ? (isArabic ? "تم الرفع" : "Uploaded") : requirement.isMandatory ? (isArabic ? "مطلوب" : "Upload required") : (isArabic ? "اختياري" : "Optional")}</span>{!received ? <label className="beneficiary-required-upload"><input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={Boolean(requirementUploads[uploadKey])} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; uploadRequestedDocument(request, requirement, file); }} /><i className={`fa-solid ${requirementUploads[uploadKey] ? "fa-spinner fa-spin" : "fa-upload"}`}></i>{requirementUploads[uploadKey] ? (isArabic ? "جارٍ الرفع..." : "Uploading...") : (isArabic ? "رفع البديل" : "Upload replacement")}</label> : null}</article>;
                                      })}
                                    </div>
                                    {requestActionMessages[request.id] ? <p className="beneficiary-action-message" role="status">{requestActionMessages[request.id]}</p> : null}
                                    <button className="beneficiary-submit-updates" type="button" disabled={(request.actionRequired || []).some((requirement) => requirement.isMandatory && String(requirement.status).toUpperCase() !== "RECEIVED")} onClick={() => submitApplicantUpdates(request)}><i className="fa-solid fa-paper-plane"></i>{isArabic ? "إرسال التحديثات" : "Submit Updates"}</button>
                                  </section>
                                ) : null}
                                <div className="aid-request-list-actions">
                                  {request.isDraft ? <><button type="button" className="aid-request-chat-link" disabled={!request.invitationId} onClick={() => request.invitationId && setChatRequest(request)}><i className="fa-regular fa-comments"></i>{isArabic ? "محادثة مع موظف تراحم" : "Chat with Tarahum Employee"}</button><Link to={request.continueUrl || "/aid-application"} className="aid-request-details-link aid-request-continue-link"><i className="fa-solid fa-pen-to-square"></i>{isArabic ? "متابعة الطلب وإرساله" : "Continue and submit application"}<i className={`fa-solid ${isArabic ? "fa-arrow-left" : "fa-arrow-right"}`}></i></Link></> : <>
                                    {getRequestCustomerStatusKey(request) !== "expired" ? <button type="button" className="aid-request-chat-link" onClick={() => setChatRequest(request)}><i className="fa-regular fa-comments"></i>{isArabic ? "محادثة مع موظف تراحم" : "Chat with Tarahum Employee"}{chatUnreadCounts[String(request.id)] ? <span className="beneficiary-chat-unread" aria-label={isArabic ? "رسائل غير مقروءة" : "Unread messages"}>{chatUnreadCounts[String(request.id)] > 99 ? "99+" : chatUnreadCounts[String(request.id)]}</span> : null}</button> : null}
                                    <Link to={`/aid-request-details?requestno=${encodeURIComponent(request.requestno || "")}`} className="aid-request-details-link">{isArabic ? "عرض جميع التفاصيل" : "View all details"}<i className={`fa-solid ${isArabic ? "fa-arrow-left" : "fa-arrow-right"}`}></i></Link>
                                  </>}
                                </div>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <div className="aid-request-empty">
                            <i className="fa-regular fa-folder-open"></i>
                            <h5>{aidRequestItems.length ? (isArabic ? "لا توجد طلبات تطابق عوامل التصفية" : "No requests match your filters") : (isArabic ? "لم يتم تقديم أو حفظ أي طلبات مساعدة بعد" : "No aid requests or drafts yet")}</h5>
                            <p>{aidRequestItems.length ? (isArabic ? "جرّب تغيير البحث أو مسح عوامل التصفية." : "Try changing your search or clearing the filters.") : (isArabic ? "ستظهر طلباتك المحفوظة والمقدمة هنا." : "Your saved and submitted applications will appear here.")}</p>
                          </div>
                        )}
                        {dashboardError ? <p className="dashboard-live-error aid-request-list-error">{dashboardError}</p> : null}
                      </div>
                    </div>
                  </div>
                  ) : null}
                  {aidWorkspaceView === "tracking" ? (
                    <section className="aid-workspace-panel" role="tabpanel">
                      <div className="aid-workspace-panel-head"><div><h3>{isArabic ? "تتبع الطلب" : "Application Tracking"}</h3><p>{isArabic ? "أدخل رقم الطلب للاطلاع على أحدث حالته ومراحله." : "Enter a request number to view its latest status and progress."}</p></div></div>
                      <div className="applictho-trk-wrap">
                        <form className="trk-search-card" onSubmit={trackAidRequest}>
                          <div className="trk-input-group">
                            <i className="fa-regular fa-file-lines"></i>
                            <input type="text" placeholder={isArabic ? "أدخل رقم الطلب" : "Enter your request number"} value={trackingRequestNo} onChange={(event) => setTrackingRequestNo(event.target.value)} autoComplete="off" />
                          </div>
                          <button className="trk-btn" type="submit" disabled={trackingLoading}><i className="fa-solid fa-magnifying-glass"></i>{trackingLoading ? (isArabic ? "جارٍ التحقق..." : "Checking...") : (isArabic ? "تتبع حالة الطلب" : "Track Request Status")}</button>
                        </form>
                      </div>
                      {trackingMessage ? <p className="track-aid-message" role="alert">{trackingMessage}</p> : null}
                      <AidTrackingResult request={trackingRequest} language={language} />
                    </section>
                  ) : null}
                  {aidWorkspaceView === "history" ? (
                    <section className="aid-workspace-panel" role="tabpanel">
                      <div className="aid-workspace-panel-head"><div><h3>{isArabic ? "سجل المساعدات" : "Aid History"}</h3><p>{isArabic ? "سجل مباشر لجميع طلبات المساعدة السابقة وحالتها." : "A live record of previous assistance requests and their outcomes."}</p></div></div>
                      <div className="aid-history-summary">
                        <div><span>{isArabic ? "إجمالي الطلبات" : "Total requests"}</span><strong>{dashboardData.totalRequests}</strong></div>
                        <div><span>{isArabic ? "الطلبات النشطة" : "Active requests"}</span><strong>{dashboardData.activeRequests}</strong></div>
                        <div><span>{isArabic ? "الطلبات المكتملة" : "Completed requests"}</span><strong>{dashboardData.completedRequests}</strong></div>
                      </div>
                      {dashboardLoading ? <div className="aid-request-empty"><i className="fa-solid fa-spinner fa-spin"></i><h5>{isArabic ? "جارٍ تحميل السجل..." : "Loading aid history..."}</h5></div> : dashboardData.requests.length ? (
                        <div className="aid-request-list aid-history-list">
                          {[...dashboardData.requests].sort((first, second) => getRequestTime(second) - getRequestTime(first)).map((request, index) => (
                            <article className="aid-request-list-card" key={`history-${request.requestno || index}`}>
                              <div className="aid-request-list-card-top"><div className="aid-request-number"><span>{isArabic ? "رقم الطلب" : "Request number"}</span><h5>{request.requestno || "—"}</h5></div><span className={`aid-request-status ${getRequestStatusClass(request)}`}>{getRequestCustomerStatus(request, isArabic)}</span></div>
                              <div className="aid-request-list-details"><div><i className="fa-solid fa-hand-holding-heart"></i><span>{isArabic ? "نوع المساعدة" : "Aid type"}</span><strong>{getRequestType(request)}</strong></div><div><i className="fa-regular fa-calendar"></i><span>{isArabic ? "تاريخ التقديم" : "Submitted date"}</span><strong>{getRequestDate(request) || "—"}</strong></div><div><i className="fa-solid fa-money-bill-wave"></i><span>{isArabic ? "المبلغ" : "Amount"}</span><strong>{formatRequestAmount(request, language)}</strong></div></div>
                              <div className="aid-request-list-actions"><Link to={`/aid-request-details?requestno=${encodeURIComponent(request.requestno || "")}`} className="aid-request-details-link">{isArabic ? "عرض التفاصيل" : "View details"}<i className={`fa-solid ${isArabic ? "fa-arrow-left" : "fa-arrow-right"}`}></i></Link></div>
                            </article>
                          ))}
                        </div>
                      ) : <div className="aid-request-empty"><i className="fa-regular fa-folder-open"></i><h5>{isArabic ? "لا يوجد سجل مساعدات بعد" : "No aid history yet"}</h5></div>}
                      {dashboardError ? <p className="dashboard-live-error aid-request-list-error">{dashboardError}</p> : null}
                    </section>
                  ) : null}
                </div>
              </div>
              <div className="beni-tab-content" id="appointment-tab">
                <AppointmentManager language={language} profile={dashboardData.profile} />
              </div>
              <div className="beni-tab-content" id="notifications-tab">
                <div className="content-card">
                  <div className="card-title">
                    <h2>Notifications &amp; Messages</h2>
                  </div>
                  <section className="notify-sec-wrap">
                    <div className="notify-grid-wrap">
                      <div className="notify-box-card">
                        <div className="notify-box-head">
                          <div>
                            <h3>Notification Types</h3>
                            <p>System generated beneficiary notifications</p>
                          </div>
                        </div>
                        <div className="notify-list-item">
                          <div className="notify-list-icon green">
                            <i className="fa-solid fa-file-circle-check"></i>
                          </div>
                          <div className="notify-list-content">
                            <h5>Request Updates</h5>
                            <span>Updates related to submitted assistance requests</span>
                          </div>
                        </div>
                        <div className="notify-list-item">
                          <div className="notify-list-icon orange">
                            <i className="fa-solid fa-id-card"></i>
                          </div>
                          <div className="notify-list-content">
                            <h5>Document Expiry Alerts</h5>
                            <span>Alerts for expired or expiring documents</span>
                          </div>
                        </div>
                        <div className="notify-list-item">
                          <div className="notify-list-icon blue">
                            <i className="fa-solid fa-calendar-check"></i>
                          </div>
                          <div className="notify-list-content">
                            <h5>Appointment Reminders</h5>
                            <span>Notifications about upcoming appointments</span>
                          </div>
                        </div>
                        <div className="notify-list-item">
                          <div className="notify-list-icon teal">
                            <i className="fa-solid fa-money-bill-wave"></i>
                          </div>
                          <div className="notify-list-content">
                            <h5>Payment Notifications</h5>
                            <span>Alerts regarding approved payment transfers</span>
                          </div>
                        </div>
                      </div>
                      <div className="notify-box-card">
                        <div className="notify-box-head">
                          <div>
                            <h3>Delivery Channels</h3>
                            <p>Available communication methods</p>
                          </div>
                        </div>
                        <div className="notify-channel-grid">
                          <div className="notify-channel-card active">
                            <div className="notify-channel-icon">
                              <i className="fa-solid fa-message"></i>
                            </div>
                            <h5>SMS</h5>
                          </div>
                          <div className="notify-channel-card active">
                            <div className="notify-channel-icon blue">
                              <i className="fa-solid fa-envelope"></i>
                            </div>
                            <h5>Email</h5>
                          </div>
                          <div className="notify-channel-card">
                            <div className="notify-channel-icon orange">
                              <i className="fa-solid fa-mobile-screen"></i>
                            </div>
                            <h5>Push Notification</h5>
                          </div>
                          <div className="notify-channel-card active">
                            <div className="notify-channel-icon whatsapp">
                              <i className="fa-brands fa-whatsapp"></i>
                            </div>
                            <h5>WhatsApp</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              <div className="beni-tab-content" id="history-tab">
                <div className="content-card">
                  <div className="card-title">
                    <h2>Aid History</h2>
                  </div>
                  <section className="aidhis-main-wrap">
                    <div className="aidhis-summary-grid">
                      <div className="aidhis-summary-card">
                        <div className="aidhis-summary-icon">
                          <i className="fa-solid fa-hand-holding-dollar"></i>
                        </div>
                        <div>
                          <span>Total Financial Aid</span>
                          <h3>AED 24,500</h3>
                        </div>
                      </div>
                      <div className="aidhis-summary-card">
                        <div className="aidhis-summary-icon blue">
                          <i className="fa-solid fa-heart-pulse"></i>
                        </div>
                        <div>
                          <span>Medical Support</span>
                          <h3>05 Cases</h3>
                        </div>
                      </div>
                      <div className="aidhis-summary-card">
                        <div className="aidhis-summary-icon orange">
                          <i className="fa-solid fa-graduation-cap"></i>
                        </div>
                        <div>
                          <span>Education Aid</span>
                          <h3>03 Approved</h3>
                        </div>
                      </div>
                      <div className="aidhis-summary-card">
                        <div className="aidhis-summary-icon green">
                          <i className="fa-solid fa-user-group"></i>
                        </div>
                        <div>
                          <span>Active Sponsors</span>
                          <h3>02 Sponsors</h3>
                        </div>
                      </div>
                    </div>
                    <div className="aidhis-grid-wrap">
                      <div className="aidhis-box-card">
                        <div className="aidhis-box-head">
                          <div className="aidhis-box-icon">
                            <i className="fa-solid fa-money-bill-wave"></i>
                          </div>
                          <div>
                            <h3>Financial Assistance</h3>
                            <p>Financial aid support history</p>
                          </div>
                        </div>
                        <div className="aidhis-detail-list">
                          <div className="aidhis-detail-item">
                            <span>Amount</span>
                            <strong>AED 12,500</strong>
                          </div>
                          <div className="aidhis-detail-item">
                            <span>Date</span>
                            <strong>12 May 2026</strong>
                          </div>
                          <div className="aidhis-detail-item">
                            <span>Status</span>
                            <label className="aidhis-status green">Approved</label>
                          </div>
                        </div>
                      </div>
                      <div className="aidhis-box-card">
                        <div className="aidhis-box-head">
                          <div className="aidhis-box-icon blue">
                            <i className="fa-solid fa-hospital"></i>
                          </div>
                          <div>
                            <h3>Medical Assistance</h3>
                            <p>Medical treatment and hospital support</p>
                          </div>
                        </div>
                        <div className="aidhis-detail-list">
                          <div className="aidhis-detail-item">
                            <span>Hospital</span>
                            <strong>Dubai Medical Center</strong>
                          </div>
                          <div className="aidhis-detail-item">
                            <span>Coverage Amount</span>
                            <strong>AED 8,000</strong>
                          </div>
                        </div>
                      </div>
                      <div className="aidhis-box-card">
                        <div className="aidhis-box-head">
                          <div className="aidhis-box-icon orange">
                            <i className="fa-solid fa-school"></i>
                          </div>
                          <div>
                            <h3>Education Assistance</h3>
                            <p>Educational and scholarship support</p>
                          </div>
                        </div>
                        <div className="aidhis-detail-list">
                          <div className="aidhis-detail-item">
                            <span>School Fees</span>
                            <strong>AED 5,400</strong>
                          </div>
                          <div className="aidhis-detail-item">
                            <span>Scholarship Support</span>
                            <strong>Partial Scholarship</strong>
                          </div>
                        </div>
                      </div>
                      <div className="aidhis-box-card">
                        <div className="aidhis-box-head">
                          <div className="aidhis-box-icon teal">
                            <i className="fa-solid fa-basket-shopping"></i>
                          </div>
                          <div>
                            <h3>Food Assistance</h3>
                            <p>Food support and voucher distribution</p>
                          </div>
                        </div>
                        <div className="aidhis-detail-list">
                          <div className="aidhis-detail-item">
                            <span>Food Vouchers</span>
                            <strong>Monthly Voucher</strong>
                          </div>
                          <div className="aidhis-detail-item">
                            <span>Distribution Dates</span>
                            <strong>Every 1st Week</strong>
                          </div>
                        </div>
                      </div>
                      <div className="aidhis-box-card full-width">
                        <div className="aidhis-box-head">
                          <div className="aidhis-box-icon green">
                            <i className="fa-solid fa-handshake"></i>
                          </div>
                          <div>
                            <h3>Sponsorship History</h3>
                            <p>Beneficiary sponsorship details and duration</p>
                          </div>
                        </div>
                        <div className="aidhis-sponsor-grid">
                          <div className="aidhis-sponsor-card">
                            <span>Sponsor Name</span>
                            <h5>Al Noor Charity Foundation</h5>
                          </div>
                          <div className="aidhis-sponsor-card">
                            <span>Sponsorship Duration</span>
                            <h5>Jan 2025 - Dec 2026</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              <div className="beni-tab-content" id="Settings-tab">
                <BeneficiaryAccountSettings
                  language={language}
                  currentEmail={dashboardData.profile.email || ""}
                  onEmailChanged={(email) => setDashboardData((current) => ({ ...current, profile: { ...current.profile, email } }))}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>
      {aiDocumentModal && typeof document !== "undefined" ? createPortal((
        <div className={`aid-ai-modal is-open ${aiDocumentModal.state === "loading" ? "is-loading" : `is-${aiDocumentModal.state}`}`} role="dialog" aria-modal="true" aria-labelledby="requestedDocumentAiModalTitle" onMouseDown={() => aiDocumentModal.state !== "loading" && setAiDocumentModal(null)}>
          <div className="aid-ai-modal-card" onMouseDown={(event) => event.stopPropagation()}>
            {aiDocumentModal.state !== "loading" ? <button className="aid-ai-close" type="button" aria-label="Close popup" onClick={() => setAiDocumentModal(null)}><i className="fa-solid fa-xmark"></i></button> : null}
            <div className="aid-ai-robot"><img src="/images/img/rahma-ai.png" alt="" />{aiDocumentModal.state !== "loading" ? <span className={`aid-ai-status-mark is-${aiDocumentModal.state}`}><i className={`fa-solid ${aiDocumentModal.state === "accepted" ? "fa-check" : aiDocumentModal.state === "rejected" ? "fa-xmark" : "fa-clock"}`}></i></span> : null}</div>
            {aiDocumentModal.state === "loading" ? <><div className="aid-ai-loader" aria-hidden="true"></div><h3 id="requestedDocumentAiModalTitle">{documentLoadingCopy.title}</h3><p>{documentLoadingCopy.message}</p></> : <>
              <h3 id="requestedDocumentAiModalTitle">{aiModalDetails?.title}</h3><p>{aiModalDetails?.message}</p>
              <div className="aid-ai-notice">{aiModalDetails?.notice}</div><div className="aid-ai-actions"><button className="aid-ai-primary" type="button" onClick={() => setAiDocumentModal(null)}>{aiModalDetails?.button}</button></div>
            </>}
          </div>
        </div>
      ), document.body) : null}
      {chatRequest && typeof document !== "undefined" ? createPortal(<RequestChatModal request={chatRequest} language={language} onClose={() => setChatRequest(null)} onMessagesRead={(requestId) => setChatUnreadCounts((current) => ({ ...current, [String(requestId)]: 0 }))} />, document.body) : null}
      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
